import type { DailyBonusCardState } from '../components/DailyBonusCard';
import type { GroupedReward } from '@/queries/rewards/rewards.schema';
import { pad } from '@/utils/parsing.utils';
import { DAILY_BONUS_MOCK_CARDS } from '../consts/mockData';

export const MOCK_REWARD_AMOUNT_SC = 7;

export type DailyBonusCardItem = {
  id: string;
  type: GroupedReward['type'];
  rewardAmount: number;
  bonusDays: number;
  order: number;
  state: DailyBonusCardState;
  onClick?: () => void;
};

// Null/NaN-safe ISO → epoch ms parser used by every window check below.
const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) {
    return null;
  }
  const ms = new Date(iso).getTime();

  return Number.isNaN(ms) ? null : ms;
};

// True while `nowMs` sits inside `[startsAt, endsAt)` — the claim window is open.
export const isGroupActive = (group: GroupedReward, nowMs: number): boolean => {
  const startsAt = toMs(group.startsAt);
  const endsAt = toMs(group.endsAt);

  return startsAt !== null && endsAt !== null && startsAt <= nowMs && nowMs < endsAt;
};

// After a claim (optimistic or backend), `startsAt` is bumped past `nowMs` —
// that's our signal that the current window has been claimed.
export const isGroupClaimed = (group: GroupedReward, nowMs: number): boolean => {
  const startsAt = toMs(group.startsAt);

  return startsAt !== null && startsAt > nowMs;
};

// ISO timestamp of the soonest future `startsAt` across all groups — feeds the countdown.
export const getNextWindowStartIso = (groups: GroupedReward[], nowMs: number): string | null => {
  const upcoming = groups
    .map(group => toMs(group.startsAt))
    .filter((ms): ms is number => ms !== null && ms > nowMs)
    .sort((a, b) => a - b);

  return upcoming[0] ? new Date(upcoming[0]).toISOString() : null;
};

const CHICAGO_UTC_OFFSET_HOURS = -5;

// Daily bonus resets at 00:00 in the UTC-5 Chicago business day, independently of reward card clicks.
export const getNextChicagoMidnightIso = (fromMs = Date.now()): string => {
  const offsetMs = CHICAGO_UTC_OFFSET_HOURS * 60 * 60 * 1000;
  const chicagoTime = new Date(fromMs + offsetMs);
  const nextChicagoMidnightUtc = Date.UTC(
    chicagoTime.getUTCFullYear(),
    chicagoTime.getUTCMonth(),
    chicagoTime.getUTCDate() + 1,
  ) - offsetMs;

  return new Date(nextChicagoMidnightUtc).toISOString();
};

// Maps the three boolean flags to the card's visual state. Order matters:
// out-of-stock beats claimed beats active.
export const getCardState = (
  isClaimed: boolean,
  isLockedByAmount: boolean,
  isActive: boolean,
): DailyBonusCardState => {
  if (isLockedByAmount) {
    return 'locked';
  }

  if (isClaimed) {
    return 'claimed';
  }

  return isActive ? 'ready' : 'locked';
};

// Renders timer hours/minutes/seconds as zero-padded `HH:MM:SS`; null when the timer is inactive.
export const formatHoursMinutesSeconds = (
  timeComponents: { hours: number; minutes: number } | null,
): string | null => {
  if (!timeComponents) {
    return null;
  }

  return `${pad(timeComponents.hours)}:${pad(timeComponents.minutes)}`;
};

// Placeholder cards shown when the API has no groups (guest user or pre-fetch) so
// the grid layout still renders.
export const buildMockDailyBonusCards = (): DailyBonusCardItem[] =>
  DAILY_BONUS_MOCK_CARDS.map((card, index) => ({
    id: `mock-${index}`,
    order: 0,
    type: 'bundle-bonus',
    rewardAmount: MOCK_REWARD_AMOUNT_SC,
    bonusDays: card.activations,
    state: card.state,
  }));

// Render order: daily-bonus card first, bundle-bonus cards after, both sorted by priority.
export const orderGroupsForRender = (groups: GroupedReward[]): GroupedReward[] => {
  const sorted = [...groups].sort((a, b) => b.priority - a.priority);
  const dailyBonus = sorted.find(group => group.type === 'daily-bonus');
  const bundleBonus = sorted.filter(group => group.type === 'bundle-bonus');

  return dailyBonus ? [...bundleBonus, dailyBonus] : bundleBonus;
};
